import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isGuideDropdownOpen, setIsGuideDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (guideDropdownRef.current && !guideDropdownRef.current.contains(e.target as Node)) {
        setIsGuideDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const guideArticles = [
    { title: 'Complete Guide 2026', path: '/sugargoo-spreadsheet-guide', icon: 'ri-book-open-line' },
    { title: 'Sugargoo Review', path: '/sugargoo-review-2026', icon: 'ri-star-line' },
    { title: 'Shipping Guide', path: '/sugargoo-shipping-guide', icon: 'ri-ship-line' },
    { title: 'Safety & Legitimacy', path: '/sugargoo-safety-legit', icon: 'ri-shield-check-line' },
    { title: 'Coupon & Discount', path: '/sugargoo-coupon-guide', icon: 'ri-ticket-2-line' },
    { title: 'Beginner Tutorial', path: '/sugargoo-beginner-tutorial', icon: 'ri-user-star-line' },
    { title: 'Haul Building Guide', path: '/sugargoo-haul-guide', icon: 'ri-shopping-basket-line' },
    { title: 'QC Photo Guide', path: '/sugargoo-qc-guide', icon: 'ri-image-line' },
    { title: 'vs Alternatives', path: '/sugargoo-vs-alternatives', icon: 'ri-scales-line' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
            <span className="text-lg md:text-xl font-heading font-semibold text-foreground-900 tracking-tight whitespace-nowrap">
              Sugargoo <span className="text-primary-600">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Home
            </Link>

            <div ref={guideDropdownRef} className="relative">
              <button
                onClick={() => {
                  setIsGuideDropdownOpen(!isGuideDropdownOpen);
                  setIsDropdownOpen(false);
                }}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-sm w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isGuideDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isGuideDropdownOpen && (
                <div className="absolute left-0 mt-2 w-64 bg-background-50 border border-background-200/70 rounded-lg py-2 max-h-80 overflow-y-auto shadow-sm">
                  {guideArticles.map((article) => (
                    <Link
                      key={article.path}
                      to={article.path}
                      onClick={() => setIsGuideDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className={`${article.icon} text-primary-500`}></i>
                      </span>
                      {article.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a
              href="#categories"
              className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Categories
            </a>
            <a
              href="#what-is"
              className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Guide
            </a>
            <a
              href="#review"
              className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Review
            </a>
            <a
              href="#shipping"
              className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Shipping
            </a>
            <a
              href="#articles"
              className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              Articles
            </a>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => {
                  setIsDropdownOpen(!isDropdownOpen);
                  setIsGuideDropdownOpen(false);
                }}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Browse Categories
                <i className={`ri-arrow-down-s-line text-sm w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-background-50 border border-background-200/70 rounded-lg py-2 max-h-96 overflow-y-auto shadow-sm">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setIsDropdownOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors whitespace-nowrap"
                    >
                      <span className="w-8 h-8 flex-shrink-0 rounded-md overflow-hidden">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </span>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold px-5 py-2 bg-foreground-900 text-background-50 rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 cursor-pointer flex-shrink-0"
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-home-line text-primary-500"></i>
              </span>
              Home
            </Link>

            <a
              href="#categories"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-lg whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-grid-line text-primary-500"></i>
              </span>
              Categories
            </a>

            <div className="pt-2">
              <p className="px-4 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                Shopping Guides
              </p>
              {guideArticles.map((article) => (
                <Link
                  key={article.path}
                  to={article.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${article.icon} text-primary-500`}></i>
                  </span>
                  {article.title}
                </Link>
              ))}
            </div>

            <div className="pt-2">
              <p className="px-4 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                Product Categories
              </p>
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-lg whitespace-nowrap"
                >
                  <span className="w-8 h-8 flex-shrink-0 rounded-md overflow-hidden">
                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                  </span>
                  <span>{cat.name}</span>
                </a>
              ))}
            </div>

            <div className="pt-3 px-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center text-sm font-semibold px-5 py-3 bg-foreground-900 text-background-50 rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}